// src/components/AdvancedEditor.tsx
import { Button, Textarea } from '@commenda/kakashi';
import { TJsonSchemaDocument } from '@commenda/types';
import { useEffect, useState } from 'react';
import { useNotification } from '../../hooks/useNotification';
import { JsonSchemaConverterHelper } from '../helpers';
import Card from './ui/Card';
import SectionTitle from './ui/SectionTitle';

export default function AdvancedEditor({
  schemaDocument,
  onSchemaDocumentChange,
}: {
  schemaDocument: TJsonSchemaDocument;
  onSchemaDocumentChange: (d: TJsonSchemaDocument) => void;
}) {
  const { setNotification } = useNotification();
  const [text, setText] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const apply = () => {
    try {
      const parsed = JSON.parse(text);
      const imported = JsonSchemaConverterHelper.importFromJSONSchema(
        parsed,
        schemaDocument.name
      );
      onSchemaDocumentChange(imported);
      setError(null);
      setNotification({
        title: 'Success',
        message: 'Schema applied to Simple editor successfully',
        valid: true,
        show: true,
      });
    } catch (e: any) {
      setError(e.message ?? String(e));
    }
  };

  const validateOnly = () => {
    try {
      JSON.parse(text);
      setNotification({
        title: 'Valid JSON',
        message: 'JSON is valid',
        valid: true,
        show: true,
      });
      setError(null);
    } catch (e: any) {
      setError(e.message ?? String(e));
    }
  };

  useEffect(() => {
    if (!schemaDocument) return;
    setText(
      JSON.stringify(
        JsonSchemaConverterHelper.buildJSONSchema(schemaDocument),
        null,
        2
      )
    );
  }, [schemaDocument]);

  return (
    <div className="space-y-4">
      <Card>
        <SectionTitle>Paste / Edit JSON Schema</SectionTitle>
        <Textarea
          className="w-full h-[480px]"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        {error && <div className="mt-2 text-sm text-red-400">{error}</div>}
        <div className="mt-3 flex gap-2">
          <Button size="sm" onClick={apply}>
            Apply to Simple Editor
          </Button>
          <Button size="sm" onClick={validateOnly}>
            Validate JSON
          </Button>
        </div>
      </Card>
    </div>
  );
}
